import {
  priceDateKey,
  storedDateToLocalDate,
} from '@/utils/circuits/prices/priceDateKey';
import { addDays, getDay, isBefore } from 'date-fns';
import { WEEKDAYS } from '@/constants/weekdays';
import {
  HOTEL_PENSION_TYPES,
  toHotelPensionType,
} from '@/constants/hotelPension';
import type {
  HotelPensionPricing,
  HotelPensionType,
} from '@/types/benefits/hotel/HotelResponse';

export interface PensionPricePerPerson {
  adultPrice: number;
  childPrice: number;
  adolescentPrice: number;
}

/**
 * Pension types above the room's base (single room or undefined ⇒ all).
 * Accepts RoomPensionOptionValue or legacy translated label.
 */
export function getPensionTypesAbove(
  standardPensionValue: string | undefined,
): HotelPensionType[] {
  if (!standardPensionValue) return [...HOTEL_PENSION_TYPES];
  if (standardPensionValue === 'SingleRoom') return [...HOTEL_PENSION_TYPES];
  const standardType = toHotelPensionType(standardPensionValue);
  if (!standardType) return [...HOTEL_PENSION_TYPES];
  const idx = HOTEL_PENSION_TYPES.indexOf(standardType);
  return HOTEL_PENSION_TYPES.slice(idx + 1);
}

/**
 * Build a lookup map of per-person pension prices for all applicable dates.
 * Iterates cards/periods by day, respects applicationDays; for each date keeps
 * the cheapest (adult+child+adolescent sum), like computeBenefitPrices.
 */
export function buildHotelPensionPriceMap(
  pricings: HotelPensionPricing[],
  pensionType: HotelPensionType,
): Map<string, PensionPricePerPerson> {
  const pricesForDate = new Map<string, PensionPricePerPerson>();

  for (const card of pricings) {
    const priceEntry = (card.prices ?? []).find(
      (p) => p.pensionType === pensionType,
    );
    if (!priceEntry) continue;

    for (const period of card.periods) {
      if (!period.startDate || !period.endDate) continue;

      const periodStart = storedDateToLocalDate(period.startDate);
      const periodEnd = storedDateToLocalDate(period.endDate);
      const dayAfterEnd = addDays(periodEnd, 1);

      let currDate = periodStart;
      while (isBefore(currDate, dayAfterEnd)) {
        const dayIndex = (getDay(currDate) + 6) % 7;
        const dayName = WEEKDAYS[dayIndex];

        if (period.applicationDays.includes(dayName)) {
          const dateKey = priceDateKey(currDate);
          const adultPrice = priceEntry.adultPrice ?? 0;
          const candidate = {
            adultPrice,
            childPrice: priceEntry.childPrice ?? adultPrice,
            adolescentPrice: priceEntry.adolescentPrice ?? adultPrice,
          };
          const candidateSum =
            candidate.adultPrice +
            candidate.childPrice +
            candidate.adolescentPrice;
          const existing = pricesForDate.get(dateKey);
          const existingSum = existing
            ? existing.adultPrice +
              existing.childPrice +
              existing.adolescentPrice
            : Infinity;
          if (candidateSum < existingSum) {
            pricesForDate.set(dateKey, candidate);
          }
        }

        currDate = addDays(currDate, 1);
      }
    }
  }

  return pricesForDate;
}

/**
 * Per-person pension price for a date. Iterates cards/periods by day, respects
 * applicationDays; for each date keeps the cheapest (adult+child+adolescent sum), like computeBenefitPrices.
 */
export function getHotelPensionPriceForDate(
  pricings: HotelPensionPricing[],
  pensionType: HotelPensionType,
  date: Date,
): PensionPricePerPerson | null {
  const targetDateStr = priceDateKey(date);
  return (
    buildHotelPensionPriceMap(pricings, pensionType).get(targetDateStr) ?? null
  );
}

export interface ComputeHotelPensionPriceForStayParams {
  pricings: HotelPensionPricing[];
  pensionType: HotelPensionType;
  startDate: Date;
  nbDays: number;
  nbAdults: number;
  nbChildren: number;
  nbAdolescents: number;
  convertPrice: (
    amount: number,
    fromCurrency: string,
    toCurrency: string,
  ) => number;
  fromCurrency: string;
  toCurrency: string;
}

/** Total pension price for the stay: sum of daily prices converted to toCurrency. */
export function computeHotelPensionPriceForStay({
  pricings,
  pensionType,
  startDate,
  nbDays,
  nbAdults,
  nbChildren,
  nbAdolescents,
  convertPrice,
  fromCurrency,
  toCurrency,
}: ComputeHotelPensionPriceForStayParams): number {
  let total = 0;
  for (let i = 0; i < nbDays; i++) {
    const day = addDays(startDate, i);
    const prices = getHotelPensionPriceForDate(pricings, pensionType, day);
    if (prices) {
      total +=
        convertPrice(prices.adultPrice, fromCurrency, toCurrency) * nbAdults +
        convertPrice(prices.childPrice, fromCurrency, toCurrency) * nbChildren +
        convertPrice(prices.adolescentPrice, fromCurrency, toCurrency) *
          nbAdolescents;
    }
  }
  return total;
}

/** First non-zero per-person price for the type (card-level). Use when dates are unknown. */
export function getRepresentativePensionPrice(
  pricings: HotelPensionPricing[],
  pensionType: HotelPensionType,
): PensionPricePerPerson | null {
  for (const card of pricings) {
    if (!card.periods?.length) continue;
    const entry = (card.prices ?? []).find(
      (p) => p.pensionType === pensionType,
    );
    if (
      entry &&
      ((entry.adultPrice ?? 0) > 0 ||
        (entry.childPrice ?? 0) > 0 ||
        (entry.adolescentPrice ?? 0) > 0)
    ) {
      const adultPrice = entry.adultPrice ?? 0;
      return {
        adultPrice,
        childPrice: entry.childPrice ?? adultPrice,
        adolescentPrice: entry.adolescentPrice ?? adultPrice,
      };
    }
  }
  return null;
}

export function resolvePensionPricings(
  commonPricings: HotelPensionPricing[] | undefined,
  categoryPricings: HotelPensionPricing[] | undefined,
  areOtherPensionPricesCommon: boolean,
): HotelPensionPricing[] {
  if (areOtherPensionPricesCommon) {
    return commonPricings ?? [];
  }
  return categoryPricings?.length ? categoryPricings : (commonPricings ?? []);
}
